import { useCallback, useContext, useEffect, useRef } from 'react';
import { useInView } from 'motion/react';
import useEvents from '@/hooks/useEvents';
import { EVENT_TYPES } from '@/shared/constants/events';
import {
	DeclarationsTrackingContext,
	definedProps,
	flowTracking,
	ScreenVisitContext,
	sectionsSeenOf,
	TrackingMutedContext,
	type TrackingProps,
} from '../lib/tracking';
import { secondsSince } from '../lib/readingTime';

const DECISION_EVENTS: ReadonlySet<string> = new Set([
	EVENT_TYPES.DECLARATIONS_CTA_CLICKED,
	EVENT_TYPES.DECLARATIONS_APPROVED,
	EVENT_TYPES.DECLARATIONS_OBJECTED,
	EVENT_TYPES.DECLARATIONS_PAYMENT_REPORTED,
	EVENT_TYPES.DECLARATIONS_SCREEN_LEFT,
]);

export const useDeclarationsTrack = (scope?: TrackingProps) => {
	const context = useContext(DeclarationsTrackingContext);
	const muted = useContext(TrackingMutedContext);
	const screen = useContext(ScreenVisitContext);
	const { trackEvent } = useEvents();
	const scopeKey = JSON.stringify(scope ?? {});
	return useCallback(
		(eventName: string, props: TrackingProps = {}) => {
			if (muted) return;
			const visit = screen?.current;
			if (visit && eventName === EVENT_TYPES.DECLARATIONS_SECTION_VIEWED && typeof props.section === 'string')
				visit.sections.add(props.section);
			void trackEvent({
				eventName,
				eventPayload: definedProps({
					ui: 'nueva',
					...flowTracking(Date.now()),
					seconds_on_screen: visit ? secondsSince(visit.shownAt) : undefined,
					...(DECISION_EVENTS.has(eventName) ? sectionsSeenOf(visit) : {}),
					...context,
					...(JSON.parse(scopeKey) as TrackingProps),
					...props,
				}),
			});
		},
		[context, muted, scopeKey, screen, trackEvent],
	);
};

export const useTrackOnce = (eventName: string, props: TrackingProps, when = true) => {
	const track = useDeclarationsTrack();
	const key = JSON.stringify(props);
	const sent = useRef<string | null>(null);
	useEffect(() => {
		if (!when) {
			sent.current = null;
			return;
		}
		if (sent.current === key) return;
		sent.current = key;
		track(eventName, JSON.parse(key) as TrackingProps);
	}, [eventName, key, track, when]);
};

export const useTrackInView = <T extends Element>(eventName: string, props: TrackingProps) => {
	const ref = useRef<T>(null);
	const inView = useInView(ref, { once: true, amount: 0.4 });
	useTrackOnce(eventName, props, inView);
	return ref;
};
